import { Fragment, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  MoneyInput,
  Textarea,
  toast,
} from '@mlm/ui';
import { ACCOUNTING_JOURNAL_WRITE_PERM } from '@mlm/shared';
import {
  fetchJournal,
  listGlAccounts,
  postManualJournalEntry,
  reverseGlJournalEntry,
  type GlJournalEntry,
  type ManualJournalLineInput,
} from '../../lib/api';
import { useHasPerm } from '../../lib/auth-store';
import { Money, PageHeader } from './_shared';

/**
 * General Journal — LIVE (Phase 4 lean GL).
 *
 * A dynamic, paged view over `gl_journal_entries`: every entry the poster
 * derived from a document plus every manual entry. Append-only — the only
 * "edit" is a linked reversal, and both directions of the link render as
 * badges so a corrected entry is never mistaken for a live one.
 */

const PAGE_SIZE = 50;

/** Local calendar date — a date-only string parses as UTC midnight (8 AM Manila). */
const todayStr = () => new Date().toLocaleDateString('en-CA');

const peso = (v: string) =>
  `₱${Number(v).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** 4-dp money string → scaled integer for the balance hint (server re-asserts in Decimal). */
function toScaled(v: string): number | null {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 10000);
}

const HONESTY_FLAGS: Array<{ key: string; label: string; title: string }> = [
  { key: 'unmapped', label: 'Unmapped kind', title: 'Unknown wallet-ledger kind — booked to 6900' },
  {
    key: 'unmapped_funds',
    label: 'No register account',
    title: 'No Cash & Bank register row — booked to the by-method fallback account',
  },
  {
    key: 'cost_missing',
    label: 'Cost missing',
    title:
      'Some units on this document had no cost, so the relief is understated. A document where NOTHING was costed posts no entry at all — those are counted on the Overview instead.',
  },
];

export function JournalPage() {
  const canWrite = useHasPerm(ACCOUNTING_JOURNAL_WRITE_PERM);
  const qc = useQueryClient();

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [q, setQ] = useState('');
  // The overview's "needs attention" tile deep-links here with ?unmapped=1.
  const [unmappedOnly, setUnmappedOnly] = useState(
    () => new URLSearchParams(window.location.search).get('unmapped') === '1',
  );
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [showManual, setShowManual] = useState(false);
  const [reversing, setReversing] = useState<GlJournalEntry | null>(null);

  const params = useMemo(
    () => ({
      from: from || undefined,
      to: to || undefined,
      q: q.trim() || undefined,
      unmappedOnly: unmappedOnly || undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [from, to, q, unmappedOnly, page],
  );

  const journalQ = useQuery({
    queryKey: ['gl-journal', params],
    queryFn: () => fetchJournal(params),
  });
  const rows = journalQ.data?.rows ?? [];
  const total = journalQ.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const refresh = () => void qc.invalidateQueries({ queryKey: ['gl-journal'] });

  return (
    <div className="space-y-4">
      <PageHeader
        title="General Journal"
        subtitle={`${total.toLocaleString()} entr${total === 1 ? 'y' : 'ies'} · every money movement, posted double-entry`}
        actions={
          canWrite ? <Button onClick={() => setShowManual(true)}>Manual entry</Button> : undefined
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-4">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">From</label>
            <Input
              type="date"
              className="h-9"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
                setPage(0);
              }}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">To</label>
            <Input
              type="date"
              className="h-9"
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
                setPage(0);
              }}
            />
          </div>
          <div className="w-64 space-y-1">
            <label className="text-xs text-muted-foreground">Search</label>
            <Input
              className="h-9"
              placeholder="Entry no, memo, or source ref"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(0);
              }}
            />
          </div>
          <label className="flex items-center gap-2 pb-2 text-sm">
            <Checkbox
              checked={unmappedOnly}
              onCheckedChange={(v) => {
                setUnmappedOnly(v === true);
                setPage(0);
              }}
            />
            Needs attention only
          </label>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle>Entries</CardTitle>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Button
              size="sm"
              variant="outline"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Prev
            </Button>
            <span>
              {page + 1} / {pages}
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={page + 1 >= pages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {journalQ.isLoading ? (
            <p className="px-4 pb-4 text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="px-4 pb-4 text-sm text-muted-foreground">
              Nothing here yet. Entries appear as the poster books documents, or when you post a
              manual entry.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2">Entry</th>
                    <th className="px-4 py-2">Date</th>
                    <th className="px-4 py-2">Memo</th>
                    <th className="px-4 py-2">Source</th>
                    <th className="px-4 py-2 text-right">Amount</th>
                    <th className="w-24 px-4 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((e) => {
                    const amount = e.lines
                      .filter((l) => l.direction === 'D')
                      .reduce((sum, l) => sum + Number(l.amount), 0);
                    const flagged = e.lines.some((l) =>
                      HONESTY_FLAGS.some((f) => l.metadata[f.key] === true),
                    );
                    const open = expanded[e.id] === true;
                    return (
                      <Fragment key={e.id}>
                        <tr
                          className="cursor-pointer border-b last:border-0 hover:bg-muted/30"
                          onClick={() => setExpanded((m) => ({ ...m, [e.id]: !open }))}
                        >
                          <td className="px-4 py-2 font-mono text-xs">
                            <div className="flex items-center gap-2">
                              {e.entryNo}
                              {e.reversesEntryId ? <Badge variant="muted">Reversal</Badge> : null}
                              {e.reversedByEntryId ? <Badge variant="muted">Reversed</Badge> : null}
                              {flagged ? <Badge variant="warning">Attention</Badge> : null}
                            </div>
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap">{e.postingDate}</td>
                          <td className="max-w-72 truncate px-4 py-2">{e.memo ?? '—'}</td>
                          <td className="px-4 py-2">
                            <span className="font-mono text-xs">{e.sourceType}</span>
                          </td>
                          <td className="px-4 py-2 text-right">
                            <Money>{peso(String(amount))}</Money>
                          </td>
                          <td className="px-4 py-2 text-right">
                            {canWrite && !e.reversedByEntryId && !e.reversesEntryId ? (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(ev) => {
                                  ev.stopPropagation();
                                  setReversing(e);
                                }}
                              >
                                Reverse
                              </Button>
                            ) : null}
                          </td>
                        </tr>
                        {open ? (
                          <tr className="border-b bg-muted/20 last:border-0">
                            <td colSpan={6} className="px-4 py-2">
                              <table className="w-full text-xs">
                                <tbody>
                                  {e.lines.map((l) => (
                                    <tr key={l.id}>
                                      <td className="w-24 py-1 font-mono">{l.accountCode}</td>
                                      <td className="py-1">
                                        {l.accountName}
                                        {HONESTY_FLAGS.filter(
                                          (f) => l.metadata[f.key] === true,
                                        ).map((f) => (
                                          <Badge
                                            key={f.key}
                                            variant="warning"
                                            className="ml-2"
                                            title={f.title}
                                          >
                                            {f.label}
                                          </Badge>
                                        ))}
                                      </td>
                                      <td className="w-32 py-1 text-right">
                                        <Money>{l.direction === 'D' ? peso(l.amount) : ''}</Money>
                                      </td>
                                      <td className="w-32 py-1 text-right">
                                        <Money>{l.direction === 'C' ? peso(l.amount) : ''}</Money>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              <p className="mt-1 text-[10px] text-muted-foreground">
                                Source ref: <span className="font-mono">{e.sourceRef}</span>
                              </p>
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {showManual ? (
        <ManualEntryDialog
          onClose={() => setShowManual(false)}
          onDone={() => {
            setShowManual(false);
            refresh();
          }}
        />
      ) : null}
      {reversing ? (
        <ReverseDialog
          entry={reversing}
          onClose={() => setReversing(null)}
          onDone={() => {
            setReversing(null);
            refresh();
          }}
        />
      ) : null}
    </div>
  );
}

// ── Manual entry ────────────────────────────────────────────────────────────

type DraftLine = {
  key: number;
  accountCode: string;
  direction: 'D' | 'C';
  amount: string;
  memo: string;
};

function ManualEntryDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const accountsQ = useQuery({ queryKey: ['gl-accounts'], queryFn: listGlAccounts });
  const accounts = (accountsQ.data?.rows ?? []).filter((a) => a.active);

  const [postingDate, setPostingDate] = useState(todayStr());
  const [memo, setMemo] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([
    { key: 1, accountCode: '', direction: 'D', amount: '', memo: '' },
    { key: 2, accountCode: '', direction: 'C', amount: '', memo: '' },
  ]);
  const [nextKey, setNextKey] = useState(3);
  // One id per dialog open: a double-clicked Save replays server-side instead
  // of posting twice.
  const [clientRequestId] = useState(() => crypto.randomUUID());

  const patchLine = (key: number, patch: Partial<DraftLine>) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  let debits = 0;
  let credits = 0;
  let invalid = false;
  for (const l of lines) {
    const scaled = toScaled(l.amount);
    if (scaled == null || !l.accountCode) {
      invalid = true;
      continue;
    }
    if (l.direction === 'D') debits += scaled;
    else credits += scaled;
  }
  const balanced = !invalid && debits === credits && debits > 0;

  const save = useMutation({
    mutationFn: () =>
      postManualJournalEntry({
        postingDate,
        memo: memo.trim() || null,
        clientRequestId,
        lines: lines.map(
          (l): ManualJournalLineInput => ({
            accountCode: l.accountCode,
            direction: l.direction,
            amount: l.amount,
            memo: l.memo.trim() || null,
          }),
        ),
      }),
    onSuccess: (res) => {
      toast.success(res.replayed ? 'Already posted.' : `Posted ${res.entry?.entryNo ?? ''}.`);
      onDone();
    },
    onError: (e: unknown) => {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'failed';
      toast.error(
        msg === 'gl_entry_unbalanced'
          ? 'Debits and credits must be equal.'
          : msg.startsWith('gl_account_unknown')
            ? 'One of the lines names an unknown account.'
            : 'Could not post the entry.',
      );
    },
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Manual journal entry</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Posting date</label>
              <Input
                type="date"
                value={postingDate}
                onChange={(e) => setPostingDate(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Memo</label>
              <Input
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                maxLength={500}
                placeholder="Why this entry exists"
              />
            </div>
          </div>

          <div className="space-y-2">
            {lines.map((l) => (
              <div key={l.key} className="flex flex-wrap items-end gap-2">
                <div className="min-w-56 flex-1 space-y-1">
                  <label className="text-xs text-muted-foreground">Account</label>
                  <select
                    className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                    value={l.accountCode}
                    onChange={(e) => patchLine(l.key, { accountCode: e.target.value })}
                  >
                    <option value="">Select an account…</option>
                    {accounts.map((a) => (
                      <option key={a.id} value={a.code}>
                        {a.code} — {a.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="w-28 space-y-1">
                  <label className="text-xs text-muted-foreground">Side</label>
                  <select
                    className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                    value={l.direction}
                    onChange={(e) =>
                      patchLine(l.key, { direction: e.target.value === 'C' ? 'C' : 'D' })
                    }
                  >
                    <option value="D">Debit</option>
                    <option value="C">Credit</option>
                  </select>
                </div>
                <div className="w-36 space-y-1">
                  <label className="text-xs text-muted-foreground">Amount (₱)</label>
                  <MoneyInput
                    value={l.amount}
                    onChange={(e) => patchLine(l.key, { amount: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
                <div className="min-w-40 flex-1 space-y-1">
                  <label className="text-xs text-muted-foreground">Line memo</label>
                  <Input
                    className="h-9"
                    value={l.memo}
                    onChange={(e) => patchLine(l.key, { memo: e.target.value })}
                    maxLength={300}
                  />
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={lines.length <= 2}
                  onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}
                >
                  ✕
                </Button>
              </div>
            ))}
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setLines((ls) => [
                  ...ls,
                  { key: nextKey, accountCode: '', direction: 'C', amount: '', memo: '' },
                ]);
                setNextKey((k) => k + 1);
              }}
            >
              Add line
            </Button>
          </div>

          <div className="rounded-md border bg-muted/30 p-3 text-sm">
            <div className="flex justify-between">
              <span>Total debits</span>
              <Money>{(debits / 10000).toFixed(2)}</Money>
            </div>
            <div className="flex justify-between">
              <span>Total credits</span>
              <Money>{(credits / 10000).toFixed(2)}</Money>
            </div>
            {!balanced ? (
              <p className="mt-1 text-xs text-destructive">
                {invalid
                  ? 'Every line needs an account and a positive amount.'
                  : 'Debits and credits must be equal before posting.'}
              </p>
            ) : null}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} disabled={!balanced || save.isPending}>
            {save.isPending ? 'Posting…' : 'Post entry'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Reversal ────────────────────────────────────────────────────────────────

function ReverseDialog({
  entry,
  onClose,
  onDone,
}: {
  entry: GlJournalEntry;
  onClose: () => void;
  onDone: () => void;
}) {
  const [memo, setMemo] = useState('');
  const reverse = useMutation({
    mutationFn: () => reverseGlJournalEntry(entry.id, memo.trim() || null),
    onSuccess: (rev) => {
      toast.success(`Posted ${rev.entryNo} reversing ${entry.entryNo}.`);
      onDone();
    },
    onError: (e: unknown) => {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'failed';
      toast.error(
        msg === 'gl_entry_already_reversed'
          ? 'This entry is already reversed.'
          : 'Could not reverse the entry.',
      );
    },
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reverse {entry.entryNo}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Posts a new entry with every debit and credit swapped, dated today, and linked to the
            original. The original is never edited.
          </p>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Reason</label>
            <Textarea value={memo} onChange={(e) => setMemo(e.target.value)} maxLength={500} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => reverse.mutate()} disabled={reverse.isPending}>
            {reverse.isPending ? 'Posting…' : 'Reverse entry'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
